// The session store (P2.2) — Zustand binding around session.ts's pure transitions.
//
// Stack §3 chose Zustand for a specific property this file leans on: the store is "usable from
// plain TS (the orchestrator) as well as React". `useSessionStore` is a hook; `useSessionStore
// .getState()` and `.subscribe()` are not, and the orchestration that translates session changes
// into engine commands (Architecture §7) has no business being a component.
//
// Thin on purpose. Every transition is a pure function in session.ts, tested without React, without
// Zustand and without a browser; this file only decides *when* they run. If logic starts
// accumulating here rather than there, the testable surface is shrinking and that is the signal to
// push it back.
//
// **Immer is deliberately absent, and Stack §3 names it.** Immer earns its cost on deeply nested
// updates — `state.mixer.channels.a.eq.high.gain` — and M2's session document is two decks of three
// flat fields, where a spread is shorter and clearer than a producer. It is ~10x Zustand's
// footprint against a payload budget already at 64% (ADR-L4), spent on ergonomics nothing needs
// yet. **Add it when the mixer lands (M4: three channel strips × EQ bands × filter × FX slots) or
// pad kits do (M6)** — that is a deferral with a trigger, not a rejection of the Stack.

import { create } from 'zustand';

import type { DeckId } from '../platform/types/control';
import {
  clearCuePoint,
  initialSession,
  loadTrack,
  setCuePoint,
  setPlaying,
  unloadTrack,
  type LoadedTrack,
  type SessionState,
} from './session';

export interface SessionActions {
  loadTrack: (deckId: DeckId, track: LoadedTrack) => void;
  unloadTrack: (deckId: DeckId) => void;
  setCuePoint: (deckId: DeckId, frame: number) => void;
  clearCuePoint: (deckId: DeckId) => void;
  setPlaying: (deckId: DeckId, playing: boolean) => void;
  /** Test seam. Not for app code — a component that resets the whole session is doing something
   * it should be doing through a named action instead. */
  __resetForTests: () => void;
}

export type SessionStore = SessionState & SessionActions;

export const useSessionStore = create<SessionStore>((set) => ({
  ...initialSession(),

  loadTrack: (deckId, track) => {
    set((state) => loadTrack(state, deckId, track));
  },
  unloadTrack: (deckId) => {
    set((state) => unloadTrack(state, deckId));
  },
  setCuePoint: (deckId, frame) => {
    set((state) => setCuePoint(state, deckId, frame));
  },
  clearCuePoint: (deckId) => {
    set((state) => clearCuePoint(state, deckId));
  },
  setPlaying: (deckId, playing) => {
    set((state) => setPlaying(state, deckId, playing));
  },
  __resetForTests: () => {
    set(initialSession());
  },
}));

/**
 * Selector for one deck, for the common case.
 *
 * A selector rather than `useSessionStore()` bare, because subscribing to the whole store means
 * re-rendering on every unrelated change — deck B's track landing should not re-render deck A.
 * Architecture §7 specifies "subscription by selector" for exactly this reason.
 */
export function useDeckSession(deckId: DeckId) {
  return useSessionStore((state) => state.decks[deckId]);
}
